import { useContext, useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import SelectOrganizationPage from "../../components/pages/select-organization";
import Licenses, {
  ModalKeys as LicensesModalKeys,
} from "../../routes/licenses";
import Users, { ModalKeys as UsersModalKeys } from "../../routes/users";
import NotFound from "../../routes/not-found";
import AppLayout from "../app-layout";
import PageNavigation, { NavItem } from "../page-navigation";
import SelectOrganization from "../../routes/select-organization";
import { useIntl } from "react-intl";
import ProgressIndicator from "../progress-indicator";
import { getEnvParam } from "../../util/env";
import Dashboard, {
  ModalKeys as DashboardModalKeys,
} from "../../routes/dashboard";
import { UUID_REG_EX_PATTERN } from "../../util/uuidUtil";
import Groups, { ModalKeys as GroupsModalKeys } from "../../routes/groups";
import Roles, { ModalKeys as RolesModalKeys } from "../../routes/roles";

import Invitations, {
  ModalKeys as InvitationsModalKeys,
} from "../../routes/invitations";
import UIConfiguration from "../../ui-configuration/configuration-provider";
import NoOrganizationToManage from "../pages/no-organization-to-manage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./app-view.scss";
import SessionExpirationWatcher from "../session-expiration-watcher";
import ActionErrorNotification from "../action-error-notification";

//<editor-fold desc="Props">
export interface AppProps {
  organizationId: string | undefined;
  hasOrgsToManage: boolean;
  isReady: boolean;
  localeInPath?: string;
  externalLogout?: boolean;
}
//</editor-fold>

function isNavActive(
  match: { path: string; isExact: boolean },
  location: { pathname: string },
  modalValues: string[]
): boolean {
  let retVal: boolean = false;
  if (match) {
    if (match.isExact) {
      retVal = true;
    } else if (modalValues && modalValues.length) {
      let modal = location.pathname.replace(match.path, "");
      if (modal.startsWith("/")) {
        modal = modal.substr(1);
      }
      // at this point the modal is either modal key or selectionId/modalKey[/secondaryid]
      if (modal.indexOf("/") > 0) {
        // pick the part that is not an uuid
        const tmp = modal.split("/");
        for (let i = 0; i < tmp.length; i += 1) {
          if (!tmp[i].match(UUID_REG_EX_PATTERN)) {
            modal = tmp[i];
            break;
          }
        }
      }
      // now we have the modal key for sure
      retVal = modalValues.indexOf(modal) >= 0;
    }
  }
  return retVal;
}
function resolveEnabledModalValues(
  keys: { [key: string]: string },
  conf: any
): string[] {
  let retVal = { ...keys };
  if (conf) {
    for (let i in keys) {
      if (conf[i] === false) {
        delete retVal[i];
      }
    }
  }
  return Object.values(retVal);
}
function App(props: AppProps): JSX.Element {
  //<editor-fold desc="Local variables">
  let {
    organizationId,
    hasOrgsToManage,
    isReady,
    localeInPath,
    externalLogout,
  } = props;
  const intl = useIntl();
  const [reactAppBasePath, setReactAppBasePath] = useState(
    (localeInPath ? "/" + localeInPath : "") + getEnvParam("REACT_APP_BASE", "")
  );
  useEffect(() => {
    setReactAppBasePath(
      (localeInPath ? "/" + localeInPath : "") +
        getEnvParam("REACT_APP_BASE", "")
    );
  }, [localeInPath]);
  const { conf } = useContext(UIConfiguration);
  /**
   * Run through all modal keys and filter out the once disabled byt conf.
   */
  let groupsModalValues: string[] = useMemo(
    () =>
      resolveEnabledModalValues(GroupsModalKeys, conf?.functionality?.groups),
    [conf?.functionality?.groups]
  );
  let rolesModalValues: string[] = useMemo(
    () => resolveEnabledModalValues(RolesModalKeys, conf?.functionality?.roles),
    [conf?.functionality?.roles]
  );
  let invitationsModalValues: string[] = useMemo(
    () =>
      resolveEnabledModalValues(
        InvitationsModalKeys,
        conf?.functionality?.invitations
      ),
    [conf?.functionality?.invitations]
  );
  let usersModalValues: string[] = useMemo(
    () => resolveEnabledModalValues(UsersModalKeys, conf?.functionality?.users),
    [conf?.functionality?.users]
  );
  let licensesModalValues: string[] = useMemo(
    () =>
      resolveEnabledModalValues(
        LicensesModalKeys,
        conf?.functionality?.licenses
      ),
    [conf?.functionality?.licenses]
  );
  let dashboardModalValues: string[] = useMemo(
    () =>
      resolveEnabledModalValues(
        DashboardModalKeys,
        conf?.functionality?.dashboard
      ),
    [conf?.functionality?.dashboard]
  );
  const nav: NavItem[] = useMemo(() => {
    const retVal: NavItem[] = [];
    if (hasOrgsToManage) {
      retVal.push({
        id: "dashboard",
        icon: <FontAwesomeIcon icon={"home"} />,
        label: intl.formatMessage({
          defaultMessage: "Dashboard",
          description: "primary navigation link label",
        }),
        path: "/",
        isActive: (
          match: { path: string; isExact: boolean },
          location: { pathname: string }
        ) => isNavActive(match, location, dashboardModalValues),
      });
      if (conf?.functionality?.sections) {
        for (let i = 0; i < conf?.functionality?.sections.length; i += 1) {
          const t = conf?.functionality?.sections[i];
          if (t === "groups") {
            retVal.push({
              id: "groups",
              icon: <FontAwesomeIcon icon={"users"} fixedWidth={true} />,
              label: intl.formatMessage({
                defaultMessage: "Groups",
                description: "primary navigation link label",
              }),
              path: "/groups",
              isActive: (
                match: { path: string; isExact: boolean },
                location: { pathname: string }
              ) => isNavActive(match, location, groupsModalValues),
            });
          } else if (t === "roles") {
            retVal.push({
              id: "roles",
              icon: (
                <span className="fa-layers fa-fw">
                  <FontAwesomeIcon
                    style={{
                      transform: "translateX(-50%) scale(0.5)",
                    }}
                    icon={"user-tie"}
                    mask={"user-graduate"}
                    transform={"grow-13 right-13 up-1"}
                  />
                  <FontAwesomeIcon
                    style={{
                      transform: "translateX(50%) scale(0.5)",
                    }}
                    icon={"user-tie"}
                    mask={"user-secret"}
                    transform={"grow-13 left-13 up-1"}
                  />
                  <FontAwesomeIcon icon={"user-tie"} transform={"shrink-2.5"} />
                </span>
              ),
              label: intl.formatMessage({
                defaultMessage: "Roles",
                description: "primary navigation link label",
              }),
              path: "/roles",
              isActive: (
                match: { path: string; isExact: boolean },
                location: { pathname: string }
              ) => isNavActive(match, location, rolesModalValues),
            });
          } else if (t === "invitations") {
            retVal.push({
              id: "invitations",
              icon: <FontAwesomeIcon icon={"envelope"} fixedWidth={true} />,
              label: intl.formatMessage({
                defaultMessage: "Invitations",
                description: "primary navigation link label",
              }),
              path: "/invitations",
              isActive: (
                match: { path: string; isExact: boolean },
                location: { pathname: string }
              ) => isNavActive(match, location, invitationsModalValues),
            });
          } else if (t === "users") {
            retVal.push({
              id: "users",
              icon: <FontAwesomeIcon icon={"user"} fixedWidth={true} />,
              label: intl.formatMessage({
                defaultMessage: "Users",
                description: "primary navigation link label",
              }),
              path: "/users",
              isActive: (
                match: { path: string; isExact: boolean },
                location: { pathname: string }
              ) => isNavActive(match, location, usersModalValues),
            });
          } else if (t === "licenses") {
            retVal.push({
              id: "licenses",
              icon: <FontAwesomeIcon icon={"boxes"} fixedWidth={true} />,
              label: intl.formatMessage({
                defaultMessage: "Licenses",
                description: "primary navigation link label",
              }),
              path: "/licenses",
              isActive: (
                match: { path: string; isExact: boolean },
                location: { pathname: string }
              ) => isNavActive(match, location, licensesModalValues),
            });
          }
        }
      }
    }
    return retVal;
  }, [
    hasOrgsToManage,
    dashboardModalValues,
    groupsModalValues,
    rolesModalValues,
    invitationsModalValues,
    usersModalValues,
    licensesModalValues,
    intl,
    conf?.functionality?.sections,
  ]);

  //</editor-fold>
  /**
   * The router key-attribute will magically fix the issue where changing the basename had no effect
   */
  return (
    <Router basename={reactAppBasePath} key={reactAppBasePath}>
      <AppLayout
        disableHeader={!isReady}
        pageNavigation={
          organizationId ? <PageNavigation nav={nav} /> : undefined
        }
      >
        {isReady && (
          <>
            {organizationId ? (
              <Switch>
                <Route
                  path="/organization/select"
                  component={SelectOrganization}
                />
                {conf.functionality &&
                  conf.functionality.sections &&
                  conf.functionality.sections.includes("groups") && (
                    <Route
                      path={`/groups/:groupId(${UUID_REG_EX_PATTERN})?${
                        groupsModalValues.length
                          ? `/:modal(${groupsModalValues.join(
                              "|"
                            )})?/:secondaryId(${UUID_REG_EX_PATTERN})?`
                          : ""
                      }`}
                      exact
                      component={Groups}
                    />
                  )}
                {conf.functionality &&
                  conf.functionality.sections &&
                  conf.functionality.sections.includes("roles") && (
                    <Route
                      path={`/roles/:roleId(${UUID_REG_EX_PATTERN})?${
                        rolesModalValues.length
                          ? `/:modal(${rolesModalValues.join(
                              "|"
                            )})?/:secondaryId(${UUID_REG_EX_PATTERN})?`
                          : ""
                      }`}
                      exact
                      component={Roles}
                    />
                  )}
                {conf.functionality &&
                  conf.functionality.sections &&
                  conf.functionality.sections.includes("invitations") && (
                    <Route
                      path={`/invitations/:invitationId(${UUID_REG_EX_PATTERN})?${
                        invitationsModalValues.length
                          ? `/:modal(${invitationsModalValues.join(
                              "|"
                            )})?/:secondaryId(${UUID_REG_EX_PATTERN})?`
                          : ""
                      }`}
                      exact
                      component={Invitations}
                    />
                  )}
                {conf.functionality &&
                  conf.functionality.sections &&
                  conf.functionality.sections.includes("users") && (
                    <Route
                      path={`/users/:userId(${UUID_REG_EX_PATTERN})?${
                        usersModalValues.length
                          ? `/:modal(${usersModalValues.join("|")})?`
                          : ""
                      }`}
                      exact
                      component={Users}
                    />
                  )}
                {conf.functionality &&
                  conf.functionality.sections &&
                  conf.functionality.sections.includes("licenses") && (
                    <Route
                      path={`/licenses/:licenseId(${UUID_REG_EX_PATTERN})?${
                        licensesModalValues.length
                          ? `/:modal(${licensesModalValues.join(
                              "|"
                            )})?/:secondaryId(${UUID_REG_EX_PATTERN})?`
                          : ""
                      }`}
                      exact
                      component={Licenses}
                    />
                  )}
                <Route
                  path={`/${
                    dashboardModalValues.length
                      ? `:modal(${dashboardModalValues.join("|")})?`
                      : ""
                  }`}
                  exact
                  component={Dashboard}
                />
                <Route path="/*" component={NotFound} />
              </Switch>
            ) : hasOrgsToManage ? (
              <SelectOrganizationPage />
            ) : (
              <NoOrganizationToManage />
            )}
            <ProgressIndicator data-test-progress-indicator />
          </>
        )}
      </AppLayout>
      <div
        className={"initializing initializing-app" + (!isReady ? " show" : "")}
      >
        <div className={"initializing-body"}>
          <FontAwesomeIcon icon={"spinner"} spin size={"8x"} />
        </div>
      </div>
      <div
        id={"notifications"}
        className={
          "d-flex flex-column flex-wrap justify-content-end align-items-end"
        }
      >
        <ActionErrorNotification />
      </div>
      <SessionExpirationWatcher externalLogout={externalLogout} />
    </Router>
  );
}

export default App;
